(() => {
  const WORKER_URL = window.STOCK_WORKER_URL || '';
  const STALE_DAYS = 7;
  const STORAGE_KEY = 'bh_quote_items';
  const CURRENCY_KEY = 'bh_currency';
  const TABLE_COLUMN_COUNT = 9;

  // ── State ──────────────────────────────────────────────────────────────────

  let allItems = [];
  let stockLoaded = false;
  let currentLastUpdated = null;
  let quoteSnapshotLastUpdated = null;
  let quoteItems = loadQuote();

  // ── Boot ───────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', () => {
    fetchStock();
    bindFilters();
    bindCurrencySwitcher();
    bindDrawer();
    bindQuoteForm();
    bindAddressAutofill();
    renderDrawer();
    bindLegendPopovers();
  });

  // ── Stock fetch ────────────────────────────────────────────────────────────

  async function fetchStock() {
    console.log('[Stock List] Fetching from:', WORKER_URL);
    try {
      const res = await fetch(`${WORKER_URL}/stock`);
      console.log('[Stock List] Response status:', res.status);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      console.log('[Stock List] Data received:', {
        items: data.items?.length,
        last_updated: data.last_updated,
        is_stale: data.is_stale,
        categories: data.categories,
        brands: data.brands?.length,
      });

      currentLastUpdated = data.last_updated || null;
      allItems = sortItems(data.items || []);
      stockLoaded = true;
      reconcileQuoteWithStock();

      populateFilters(data.categories || [], data.brands || []);
      renderTable(allItems);
      renderMeta(data.last_updated, data.is_stale);

      document.getElementById('stock-list-loading').hidden = true;
      document.getElementById('stock-list-table-wrap').hidden = false;
      console.log('[Stock List] Render complete.');
    } catch (err) {
      console.error('[Stock List] Fetch error:', err);
      document.getElementById('stock-list-loading').hidden = true;
      document.getElementById('stock-list-error').hidden = false;
    }
  }

  function renderMeta(lastUpdated, isStale) {
    if (!lastUpdated) return;

    const date = new Date(lastUpdated);
    const formatted = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const badge = document.getElementById('stock-list-meta');
    const metaText = document.getElementById('stock-list-meta-text');
    if (badge && metaText) {
      metaText.textContent = `Updated ${formatted} (Refreshed Weekly)`;
      badge.hidden = false;
    }

    if (isStale) {
      const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
      const warning = document.getElementById('stock-list-stale-warning');
      const staleText = document.getElementById('stock-list-stale-text');
      if (warning && staleText) {
        staleText.textContent =
          `This list was last updated ${diffDays} days ago — contact us for the latest availability.`;
        warning.hidden = false;
      }
    }
  }

  // ── Filters ────────────────────────────────────────────────────────────────

  function populateFilters(categories, brands) {
    const catSel = document.getElementById('stock-filter-category');
    categories.forEach(c => {
      const opt = document.createElement('option');
      opt.value = c;
      opt.textContent = c;
      catSel.appendChild(opt);
    });

    const brandSel = document.getElementById('stock-filter-brand');
    brands.forEach(b => {
      const opt = document.createElement('option');
      opt.value = b;
      opt.textContent = b;
      brandSel.appendChild(opt);
    });
  }

  function activeCurrency() {
    return localStorage.getItem(CURRENCY_KEY) === 'USD' ? 'USD' : 'GBP';
  }

  function bindCurrencySwitcher() {
    const sel = document.getElementById('stock-filter-currency');
    if (!sel) return;
    // Inline preload script in stock-list.liquid already set data-currency for first paint.
    if (activeCurrency() === 'USD') sel.value = 'USD';
    sel.addEventListener('change', () => {
      const isUsd = sel.value === 'USD';
      if (isUsd) document.documentElement.dataset.currency = 'USD';
      else delete document.documentElement.dataset.currency;
      localStorage.setItem(CURRENCY_KEY, isUsd ? 'USD' : 'GBP');
      renderDrawer();
    });
  }

  function bindFilters() {
    ['stock-search', 'stock-filter-category', 'stock-filter-brand'].forEach(id => {
      document.getElementById(id).addEventListener('input', applyFilters);
    });
  }

  function applyFilters() {
    const search = document.getElementById('stock-search').value.toLowerCase().trim();
    const category = document.getElementById('stock-filter-category').value;
    const brand = document.getElementById('stock-filter-brand').value;

    const filtered = allItems.filter(item => {
      if (category && item.category !== category) return false;
      if (brand && item.brand !== brand) return false;
      if (search && ![item.description, item.part_no, item.brand, item.remarks, item.category, item.sub_category].some(v => String(v).toLowerCase().includes(search))) return false;
      return true;
    });

    renderTable(filtered);
  }

  // ── Table render ───────────────────────────────────────────────────────────

  function renderTable(items) {
    const tbody = document.getElementById('stock-list-tbody');
    const noResults = document.getElementById('stock-list-no-results');
    const count = document.getElementById('stock-list-count');

    tbody.innerHTML = '';

    count.textContent = items.length === allItems.length
      ? `${items.length} items`
      : `${items.length} of ${allItems.length} items`;

    if (!items.length) {
      noResults.hidden = false;
      return;
    }
    noResults.hidden = true;

    let lastCategory = null;
    let lastSubCategory = null;

    items.forEach(item => {
      if (item.category !== lastCategory) {
        lastCategory = item.category;
        lastSubCategory = null;
        const catRow = document.createElement('tr');
        catRow.className = 'stock-list__category-row';
        catRow.innerHTML = `<td colspan="${TABLE_COLUMN_COUNT}" class="stock-list__category-cell">${escHtml(item.category)}</td>`;
        tbody.appendChild(catRow);
      }

      if (item.sub_category !== lastSubCategory) {
        lastSubCategory = item.sub_category;
        const subRow = document.createElement('tr');
        subRow.className = 'stock-list__subcategory-row';
        subRow.innerHTML = `<td colspan="${TABLE_COLUMN_COUNT}" class="stock-list__subcategory-cell">${escHtml(item.sub_category)}</td>`;
        tbody.appendChild(subRow);
      }

      const inQuote = quoteItems.find(q => q.id === item.id);
      const itemIsLeadTime = isLeadTime(item.remarks);
      const leadTime = itemIsLeadTime ? item.remarks : '—';
      const descHtml = (item.remarks && !itemIsLeadTime)
        ? `${escHtml(item.description)} <span class="stock-list__remark">(${escHtml(item.remarks)})</span>`
        : escHtml(item.description);
      const row = document.createElement('tr');
      row.className = 'stock-list__item-row';
      row.dataset.id = item.id;
      row.innerHTML = `
        <td class="stock-list__td stock-list__td--desc">${descHtml}</td>
        <td class="stock-list__td stock-list__td--mono">${escHtml(item.part_no)}</td>
        <td class="stock-list__td stock-list__td--brand stock-list__td--center">${escHtml(item.brand)}</td>
        <td class="stock-list__td stock-list__td--remarks stock-list__td--center">${escHtml(leadTime)}</td>
        <td class="stock-list__td stock-list__td--price stock-list__td--center">
          <span class="stock-list__price stock-list__price--gbp">£${item.price_gbp.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <span class="stock-list__price stock-list__price--usd">$${(item.price_usd_sell || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </td>
        <td class="stock-list__td stock-list__td--qty-avail stock-list__td--center">${item.qty.toLocaleString()}</td>
        <td class="stock-list__td stock-list__td--qty">
          <quantity-input class="quantity">
            <button class="quantity__button no-js-hidden focus-inset" type="button" name="minus" aria-label="Decrease quantity">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="2" viewBox="0 0 10 2" fill="none"><path d="M1 1H9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
            <input
              class="quantity__input focus-inset"
              type="number"
              name="quantity"
              min="1"
              max="${item.qty}"
              value="${inQuote ? inQuote.qty_requested : 1}"
              aria-label="Quantity needed"
            >
            <button class="quantity__button no-js-hidden focus-inset" type="button" name="plus" aria-label="Increase quantity">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 1V9M1 5H9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
          </quantity-input>
        </td>
        <td class="stock-list__td stock-list__td--action">
          <button
            type="button"
            class="button button--${inQuote ? 'secondary stock-list__btn--added' : 'primary'} stock-list__add-btn"
            data-id="${item.id}"
          >
            ${inQuote ? 'Update' : 'Add to Request'}
          </button>
          <button
            type="button"
            class="stock-list__remove-btn"
            data-id="${item.id}"
            aria-label="Remove from quote"
            ${inQuote ? '' : 'hidden'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>
          </button>
        </td>
        <td class="stock-list__td stock-list__td--mobile-details">
          <details class="stock-list__accordion">
            <summary class="stock-list__accordion-summary">
              Details
              <svg class="stock-list__accordion-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
            </summary>
            <div class="stock-list__accordion-body">
              <div class="stock-list__accordion-row">
                <span class="stock-list__accordion-label">Brand</span>
                <span>${escHtml(item.brand)}</span>
              </div>
              <div class="stock-list__accordion-row">
                <span class="stock-list__accordion-label">Available</span>
                <span>${item.qty.toLocaleString()}</span>
              </div>
              <div class="stock-list__accordion-row">
                <span class="stock-list__accordion-label">Lead Time</span>
                <span>${escHtml(leadTime)}</span>
              </div>
            </div>
          </details>
        </td>`;
      tbody.appendChild(row);
    });

    bindAddButtons();
  }

  function bindAddButtons() {
    document.querySelectorAll('.stock-list__add-btn').forEach(btn => {
      const row = btn.closest('tr');
      const id = parseInt(btn.dataset.id, 10);
      const item = allItems.find(i => i.id === id);
      const qtyInput = row.querySelector('.quantity__input');
      const removeBtn = row.querySelector('.stock-list__remove-btn');
      if (!item) return;

      qtyInput.addEventListener('input', () => {
        const v = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.min(item.qty, Math.max(1, v));
      });

      btn.addEventListener('click', () => {
        const qty = Math.min(item.qty, Math.max(1, parseInt(qtyInput.value, 10) || 1));
        addToQuote(item, qty);
        btn.textContent = 'Update';
        btn.classList.remove('button--primary');
        btn.classList.add('button--secondary', 'stock-list__btn--added');
        if (removeBtn) removeBtn.hidden = false;
      });

      if (removeBtn) {
        removeBtn.addEventListener('click', () => removeFromQuote(id));
      }
    });
  }

  // ── Quote state ────────────────────────────────────────────────────────────

  function loadQuote() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (Array.isArray(stored)) return stored;
      if (stored && Array.isArray(stored.items)) {
        quoteSnapshotLastUpdated = stored.last_updated || null;
        return stored.items;
      }
    } catch {
    }
    return [];
  }

  function saveQuote() {
    if (!quoteItems.length) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      last_updated: quoteSnapshotLastUpdated,
      items: quoteItems,
    }));
  }

  function reconcileQuoteWithStock() {
    if (!stockLoaded) return;

    if (!quoteItems.length) {
      quoteSnapshotLastUpdated = currentLastUpdated;
      saveQuote();
      return;
    }

    if (currentLastUpdated && quoteSnapshotLastUpdated !== currentLastUpdated) {
      quoteItems = [];
      quoteSnapshotLastUpdated = currentLastUpdated;
      saveQuote();
      renderDrawer();
      return;
    }

    quoteItems = quoteItems
      .map(quoteItem => {
        const currentItem = allItems.find(item => item.id === quoteItem.id);
        if (!currentItem) return null;
        return buildQuoteItem(currentItem, quoteItem.qty_requested);
      })
      .filter(Boolean);
    quoteSnapshotLastUpdated = currentLastUpdated;
    saveQuote();
    renderDrawer();
  }

  function buildQuoteItem(item, qty) {
    return {
      id: item.id,
      description: item.description,
      part_no: item.part_no,
      brand: item.brand,
      qty_available: item.qty,
      price_gbp: item.price_gbp,
      price_usd_sell: item.price_usd_sell || 0,
      remarks: item.remarks,
      qty_requested: clampQuantity(qty, item.qty),
    };
  }

  function addToQuote(item, qty) {
    const existing = quoteItems.find(q => q.id === item.id);
    if (existing) {
      Object.assign(existing, buildQuoteItem(item, qty));
    } else {
      quoteItems.push(buildQuoteItem(item, qty));
    }
    quoteSnapshotLastUpdated = currentLastUpdated;
    saveQuote();
    renderDrawer();
    openDrawer();
  }

  function removeFromQuote(id) {
    quoteItems = quoteItems.filter(q => q.id !== id);
    saveQuote();
    renderDrawer();

    const btn = document.querySelector(`.stock-list__add-btn[data-id="${id}"]`);
    if (btn) {
      btn.textContent = 'Add to Request';
      btn.classList.add('button--primary');
      btn.classList.remove('button--secondary', 'stock-list__btn--added');
    }
    const removeBtn = document.querySelector(`.stock-list__remove-btn[data-id="${id}"]`);
    if (removeBtn) removeBtn.hidden = true;
    const tableInput = document.querySelector(`.stock-list__add-btn[data-id="${id}"]`)?.closest('tr')?.querySelector('.quantity__input');
    if (tableInput) tableInput.value = 1;
  }

  // ── Drawer render ──────────────────────────────────────────────────────────

  function renderDrawer() {
    const count = quoteItems.length;
    const countEl = document.getElementById('quote-drawer-count');
    const drawerEl = document.getElementById('quote-drawer');

    countEl.textContent = count;
    drawerEl.classList.toggle('quote-drawer--has-items', count > 0);

    if (count > 0) {
      drawerEl.removeAttribute('inert');
      drawerEl.setAttribute('aria-hidden', 'false');
    } else {
      drawerEl.setAttribute('inert', '');
      drawerEl.setAttribute('aria-hidden', 'true');
      closeDrawer();
    }

    const itemsEl = document.getElementById('quote-drawer-items');
    itemsEl.innerHTML = '';

    const isUsd = activeCurrency() === 'USD';
    const symbol = isUsd ? '$' : '£';
    const locale = isUsd ? 'en-US' : 'en-GB';

    quoteItems.forEach((item, index) => {
      const unitPrice = isUsd ? (item.price_usd_sell || item.price_gbp) : item.price_gbp;
      const row = document.createElement('div');
      row.className = 'quote-drawer__item';
      row.style.setProperty('--item-index', index);
      row.innerHTML = `
        <div class="quote-drawer__item-info">
          <span class="quote-drawer__item-desc">${escHtml(item.description)}</span>
          <span class="quote-drawer__item-meta body-small">${escHtml(item.part_no)} · ${escHtml(item.brand)}</span>
        </div>
        <div class="quote-drawer__item-right">
          <span class="quote-drawer__item-price">${symbol}${(unitPrice * item.qty_requested).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <span class="quote-drawer__item-qty body-small">${symbol}${unitPrice.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} × ${item.qty_requested}</span>
        </div>
        <button type="button" class="quote-drawer__remove" data-id="${item.id}" aria-label="Remove ${escHtml(item.description)}">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
          </svg>
        </button>`;
      itemsEl.appendChild(row);
    });

    itemsEl.querySelectorAll('.quote-drawer__remove').forEach(btn => {
      btn.addEventListener('click', () => removeFromQuote(parseInt(btn.dataset.id, 10)));
    });
  }

  // ── Drawer open/close ──────────────────────────────────────────────────────

  function bindDrawer() {
    const bar = document.getElementById('quote-drawer-bar');
    const closeBtn = document.getElementById('quote-drawer-close');
    const overlay = document.getElementById('quote-drawer-overlay');

    bar.addEventListener('click', toggleDrawer);
    bar.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') toggleDrawer(); });
    closeBtn.addEventListener('click', closeDrawer);
    overlay.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeDrawer();
    });
  }

  function toggleDrawer() {
    const panel = document.getElementById('quote-drawer-panel');
    panel.classList.contains('is-open') ? closeDrawer() : openDrawer();
  }

  function openDrawer() {
    if (!quoteItems.length) return;
    const panel = document.getElementById('quote-drawer-panel');
    const bar = document.getElementById('quote-drawer-bar');
    const overlay = document.getElementById('quote-drawer-overlay');
    overlay.hidden = false;
    panel.hidden = false;
    requestAnimationFrame(() => {
      overlay.classList.add('is-visible');
      panel.classList.add('is-open');
    });
    bar.setAttribute('aria-expanded', 'true');
    document.body.classList.add('quote-drawer-open');
  }

  function closeDrawer() {
    const panel = document.getElementById('quote-drawer-panel');
    const bar = document.getElementById('quote-drawer-bar');
    const overlay = document.getElementById('quote-drawer-overlay');
    overlay.classList.remove('is-visible');
    panel.classList.remove('is-open');
    window.setTimeout(() => {
      if (!overlay.classList.contains('is-visible')) overlay.hidden = true;
      if (!panel.classList.contains('is-open')) panel.hidden = true;
    }, 380);
    bar.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('quote-drawer-open');
  }

  // ── Address autofill ──────────────────────────────────────────────────────

  function bindAddressAutofill() {
    customElements.whenDefined('mapbox-address-autofill').then(() => {
      const el = document.getElementById('quote-address-autofill');
      if (!el) return;
      // retrieve fires BEFORE the component fills the input — defer with setTimeout(0).
      // Property availability varies by feature type: address-level features have
      // address_level*/postcode/country; street-level matches only have place_formatted.
      el.addEventListener('retrieve', e => {
        const p = e.detail?.features?.[0]?.properties;
        if (!p) return;
        const street = [p.address_line1, p.address_line2, p.address_line3]
          .filter(Boolean).join(' ');
        const place = p.place_formatted
          || [p.address_level2, p.address_level1, p.postcode, p.country]
               .filter(Boolean).join(', ');
        const full = p.full_address
          || p.place_name
          || [street, place].filter(Boolean).join(', ')
          || street;
        setTimeout(() => {
          document.getElementById('quote-address').value = full;
          document.getElementById('quote-address').dataset.country = p.country || '';
        }, 0);
      });
    });
  }

  // ── Quote form submit ──────────────────────────────────────────────────────

  function bindQuoteForm() {
    document.getElementById('quote-form').addEventListener('submit', async e => {
      e.preventDefault();
      submitQuote();
    });

    document.getElementById('quote-success-reset').addEventListener('click', () => {
      quoteItems = [];
      quoteSnapshotLastUpdated = currentLastUpdated;
      saveQuote();
      renderDrawer();
      closeDrawer();
      document.getElementById('quote-success').hidden = true;
      document.getElementById('quote-form').hidden = false;
      resetStockTableRows();
    });
  }

  function resetStockTableRows() {
    document.querySelectorAll('.stock-list__add-btn').forEach(btn => {
      btn.textContent = 'Add to Request';
      btn.classList.add('button--primary');
      btn.classList.remove('button--secondary', 'stock-list__btn--added');
    });
    document.querySelectorAll('.stock-list__remove-btn').forEach(btn => {
      btn.hidden = true;
    });
    document.querySelectorAll('.stock-list__table .quantity__input').forEach(input => {
      input.value = 1;
    });
  }

  async function submitQuote() {
    const form = document.getElementById('quote-form');
    const errorEl = document.getElementById('quote-form-error');
    const submitBtn = document.getElementById('quote-submit');
    const submitText = submitBtn.querySelector('.quote-drawer__submit-text');
    const submitLoading = submitBtn.querySelector('.quote-drawer__submit-loading');

    errorEl.hidden = true;

    if (!stockLoaded) {
      errorEl.textContent = 'Stock is still loading. Please try again in a moment.';
      errorEl.hidden = false;
      return;
    }

    reconcileQuoteWithStock();

    if (!quoteItems.length) {
      errorEl.textContent = 'Add at least one item to request a quote.';
      errorEl.hidden = false;
      return;
    }

    if (!form.checkValidity()) {
      const invalidField = form.querySelector(':invalid');
      errorEl.textContent = invalidField?.type === 'email'
        ? 'Please enter a valid email address.'
        : 'Please fill in all required fields.';
      errorEl.hidden = false;
      invalidField?.focus();
      return;
    }

    const contact = {
      name: document.getElementById('quote-name').value.trim(),
      company: document.getElementById('quote-company').value.trim(),
      email: document.getElementById('quote-email').value.trim(),
      phone: document.getElementById('quote-phone').value.trim(),
      address: document.getElementById('quote-address').value.trim(),
      notes: document.getElementById('quote-notes').value.trim(),
    };

    submitBtn.disabled = true;
    submitText.hidden = true;
    submitLoading.hidden = false;

    try {
      const res = await fetch(`${WORKER_URL}/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact, items: quoteItems, currency: activeCurrency() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Submission failed');

      // Clear the quote
      quoteItems = [];
      quoteSnapshotLastUpdated = currentLastUpdated;
      saveQuote();
      renderDrawer();
      resetStockTableRows();

      document.getElementById('quote-form').hidden = true;
      document.getElementById('quote-success').hidden = false;
    } catch (err) {
      errorEl.textContent = err.message || 'Something went wrong. Please try again.';
      errorEl.hidden = false;
    } finally {
      submitBtn.disabled = false;
      submitText.hidden = false;
      submitLoading.hidden = true;
    }
  }

  // ── Utils ──────────────────────────────────────────────────────────────────

  const CATEGORY_ORDER = ['SSD', 'MODULE', 'CPU'];
  const LEAD_TIME_RE = /\d+\s*[-–]?\s*\d*\s*(day|week|month|hour|yr|year)/i;
  const IN_STOCK_RE = /^in\s+stock$/i;

  function categoryPriority(cat) {
    const upper = cat.toUpperCase();
    const idx = CATEGORY_ORDER.findIndex(c => upper.includes(c));
    return idx === -1 ? CATEGORY_ORDER.length : idx;
  }

  function sortItems(items) {
    return [...items].sort((a, b) => {
      const cp = categoryPriority(a.category) - categoryPriority(b.category);
      if (cp !== 0) return cp;
      const cc = a.category.localeCompare(b.category);
      if (cc !== 0) return cc;
      const sc = (a.sub_category || '').localeCompare(b.sub_category || '');
      if (sc !== 0) return sc;
      return (a.description || '').localeCompare(b.description || '');
    });
  }

  function isLeadTime(remarks) {
    if (!remarks) return false;
    return LEAD_TIME_RE.test(remarks) || IN_STOCK_RE.test(remarks);
  }

  function clampQuantity(value, max) {
    const parsed = parseInt(value, 10) || 1;
    return Math.min(max, Math.max(1, parsed));
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── Legend popovers ────────────────────────────────────────────────────────

  function bindLegendPopovers() {
    document.querySelectorAll('.stock-list__legend-tip').forEach(btn => {
      const popover = document.getElementById(btn.getAttribute('popovertarget'));
      if (!popover) return;

      popover.addEventListener('toggle', e => {
        if (e.newState !== 'open') return;
        const GAP = 8;
        const MARGIN = 8;
        const btnRect = btn.getBoundingClientRect();
        const popRect = popover.getBoundingClientRect();
        const aboveTop = btnRect.top - popRect.height - GAP;
        const top = aboveTop >= MARGIN ? aboveTop : btnRect.bottom + GAP;
        const idealLeft = btnRect.left + btnRect.width / 2 - popRect.width / 2;
        const left = Math.max(MARGIN, Math.min(idealLeft, window.innerWidth - popRect.width - MARGIN));
        popover.style.top = `${top}px`;
        popover.style.left = `${left}px`;
      });

      btn.addEventListener('mouseenter', () => popover.showPopover());
      btn.addEventListener('mouseleave', () => popover.hidePopover());
    });
  }
})();
